import { useEffect, useMemo, useState } from 'react'
import { useSettingsRowMutations, useSettingsRows, useSettingsTable } from '@/api/queries'
import type { FieldSchema, SettingsRow } from '@/api/types'
import { DataTable, type Column } from '@/components/DataTable/DataTable'
import { Button, IconButton, LinkButton } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { Icons } from '@/components/ui/Icons'
import { Modal } from '@/components/ui/Modal'
import { LoadingBlock } from '@/components/ui/Spinner'
import { useToast } from '@/components/ui/Toast'
import { displayValue, emptyRow, fieldLabel, rowId } from './fieldValues'
import { RowForm } from './RowForm'
import styles from './settings.module.css'

interface SettingsTableDialogProps {
  tableKey: string
  /** Opens the editor on the first matching row as soon as rows are loaded. */
  focusRow?: (row: SettingsRow) => boolean
  onClose: () => void
}

// The grid shows the key fields plus the first few short fields; everything else lives in the form.
const MAX_COLUMNS = 8
const COMPACT_TYPES = new Set(['string', 'integer', 'boolean', 'enum', 'time'])

/** Generic editor for one configuration table (policies, queue storages, proxy products). */
export function SettingsTableDialog({ tableKey, focusRow, onClose }: SettingsTableDialogProps) {
  const { data: table, isLoading: tableLoading, error: tableError } = useSettingsTable(tableKey)
  const { data: rows, isLoading, error } = useSettingsRows(tableKey)
  const { remove } = useSettingsRowMutations(tableKey)
  const toast = useToast()
  const [editing, setEditing] = useState<{ row: SettingsRow; isNew: boolean } | null>(null)
  const [deleting, setDeleting] = useState<SettingsRow | null>(null)
  const [focused, setFocused] = useState(false)

  const fields = useMemo(() => table?.sections.flatMap((s) => s.fields) ?? [], [table])
  const gridFields = useMemo(() => {
    const keys = fields.filter((f) => f.isKey)
    const others = fields.filter((f) => !f.isKey && COMPACT_TYPES.has(f.type)).slice(0, Math.max(0, MAX_COLUMNS - keys.length))
    return [...keys, ...others]
  }, [fields])

  useEffect(() => {
    if (focused || !focusRow || !rows || !table) return
    setFocused(true)
    const match = rows.find(focusRow)
    if (match) setEditing({ row: match, isNew: false })
    else toast.notify('No policy row found for this POS', 'error')
  }, [focused, focusRow, rows, table, toast])

  const runDelete = async (row: SettingsRow) => {
    setDeleting(null)
    try {
      await remove.mutateAsync(rowId(fields, row))
      toast.notify('Row deleted', 'success')
    } catch (err) {
      toast.error(err, 'Delete failed')
    }
  }

  const columns: Column<SettingsRow>[] = [
    ...gridFields.map(
      (field: FieldSchema): Column<SettingsRow> => ({
        key: field.name,
        header: fieldLabel(field),
        value: (row) => displayValue(field, row[field.name]),
        render: (row) => <span className={field.isKey ? styles.key : undefined}>{displayValue(field, row[field.name])}</span>,
      }),
    ),
    {
      key: 'actions',
      header: '',
      sortable: false,
      width: table?.canDelete ? '70px' : '40px',
      render: (row) => (
        <span className="u-row">
          <IconButton label="Edit" onClick={() => setEditing({ row, isNew: false })}>
            <Icons.Edit size={14} />
          </IconButton>
          {table?.canDelete && (
            <IconButton label="Delete" onClick={() => setDeleting(row)}>
              <Icons.Trash size={14} />
            </IconButton>
          )}
        </span>
      ),
    },
  ]

  return (
    <Modal
      title={table?.name ?? 'Configuration'}
      onClose={onClose}
      size="xl"
      headerExtra={
        <div className="u-row">
          {table?.documentationUrl && (
            <LinkButton size="sm" href={table.documentationUrl} icon={<Icons.External size={12} />}>
              Docs
            </LinkButton>
          )}
          <Button size="sm" variant="primary" icon={<Icons.Plus size={12} />} disabled={!table} onClick={() => table && setEditing({ row: emptyRow(fields), isNew: true })}>
            New row
          </Button>
        </div>
      }
    >
      {tableLoading ? (
        <LoadingBlock />
      ) : (
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(row) => rowId(fields, row) + ':' + fields.filter((f) => f.isKey).map((f) => String(row[f.name] ?? '')).join('|')}
          loading={isLoading}
          error={tableError ?? error}
          searchable
          searchPlaceholder="Search in the table"
          dense
          onRowClick={(row) => setEditing({ row, isNew: false })}
          copyText={(visible) => JSON.stringify(visible, null, 2)}
          emptyMessage="No row in this table"
        />
      )}
      {editing && table && <RowForm table={table} row={editing.row} isNew={editing.isNew} onClose={() => setEditing(null)} />}
      {deleting && (
        <ConfirmDialog
          title="Delete row"
          message={`Delete ${rowId(fields, deleting)} from ${table?.name ?? 'the table'}? Policies that reference it keep the stale name.`}
          confirmLabel="Delete"
          danger
          busy={remove.isPending}
          onCancel={() => setDeleting(null)}
          onConfirm={() => void runDelete(deleting)}
        />
      )}
    </Modal>
  )
}
